/**
 * TagComboTracker — pure 2-player tag-combo detector (no Phaser).
 *
 * Records which hero hit which target and on which frame. When two different
 * heroes both hit the same target within TAG_WINDOW_FRAMES, a named cinematic
 * finisher fires (e.g. Ray + Errol = "Two Tone Storm").
 */

export interface TagFinisher {
  /** Display name for the cinematic label, e.g. "Two Tone Storm". */
  name: string;
  hero1Id: string;
  hero2Id: string;
  /** combatId of the doubly-hit target. */
  targetId: number;
}

interface HitRecord {
  hitterId: number;
  heroId: string;
  targetId: number;
  frame: number;
}

function pairKey(a: string, b: string): string {
  return a < b ? `${a}+${b}` : `${b}+${a}`;
}

const TAG_COMBOS: Record<string, string> = {
  'errol+ray': 'Two Tone Storm',
  'kris+ray': 'Concrete Mixer',
  'priya+ray': 'North Star',
  'ghost+ray': 'Phantom Punch',
  'errol+kris': 'Righteous Rage',
  'errol+priya': 'Blood & Thunder',
  'errol+ghost': 'Shadow Rhythm',
  'kris+priya': 'Iron Lotus',
  'ghost+kris': 'Spec Ops',
  'ghost+priya': 'Ghost Medic',
};

/** Frames within which two heroes must both hit the same target to trigger a finisher. */
export const TAG_WINDOW_FRAMES = 12;

export class TagComboTracker {
  private records: HitRecord[] = [];

  /**
   * Record that `hitterId`/`heroId` hit `targetId` on `frame`.
   * Returns a TagFinisher if a 2-player tag combo was detected, else null.
   * One record slot per hitter — each new hit from the same hitter replaces
   * their previous entry so only their most recent target is tracked.
   */
  recordHit(hitterId: number, heroId: string, targetId: number, frame: number): TagFinisher | null {
    this.records = this.records.filter((r) => r.hitterId !== hitterId);
    this.records.push({ hitterId, heroId, targetId, frame });
    this.records = this.records.filter((r) => frame - r.frame <= TAG_WINDOW_FRAMES);
    return this.detect(frame);
  }

  private detect(frame: number): TagFinisher | null {
    const valid = this.records.filter((r) => frame - r.frame <= TAG_WINDOW_FRAMES);
    for (let i = 0; i < valid.length; i++) {
      for (let j = i + 1; j < valid.length; j++) {
        const a = valid[i]!;
        const b = valid[j]!;
        if (a.hitterId === b.hitterId) continue;
        if (a.heroId === b.heroId) continue;
        if (a.targetId !== b.targetId) continue;
        const name = TAG_COMBOS[pairKey(a.heroId, b.heroId)];
        if (!name) continue;
        return { name, hero1Id: a.heroId, hero2Id: b.heroId, targetId: a.targetId };
      }
    }
    return null;
  }

  clear(): void {
    this.records = [];
  }
}

/** Look up the finisher name for a hero pair (order-independent). */
export function getTagComboName(heroA: string, heroB: string): string | undefined {
  return TAG_COMBOS[pairKey(heroA, heroB)];
}
